import { useState, useEffect, useCallback } from "react";

// ── CONFIG ────────────────────────────────────────────────────
const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const WA_ADMIN          = process.env.REACT_APP_WA_ADMIN || "6281234567890";

async function sbGet(path) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
  });
  if (!r.ok) return null;
  const d = await r.json();
  return Array.isArray(d) ? d : null;
}

async function sbPost(path, body) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) return null;
  const d = await r.json();
  return Array.isArray(d) ? d[0] : d;
}

// ── HELPERS ──────────────────────────────────────────────────
function fmt(n) { return "Rp " + (n || 0).toLocaleString("id-ID"); }

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso);
  const m = Math.floor(diff / 60000);
  if (m < 60)  return `${m} menit lalu`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h} jam lalu`;
  return `${Math.floor(h / 24)} hari lalu`;
}

// Komisi reseller: 20% dari total order
const COMMISSION_RATE = 0.20;

// ── CSS ───────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500&family=Cormorant+Garamond:ital,wght@0,300;1,400&family=Josefin+Sans:wght@200;300;400&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  html { scroll-behavior:smooth; }
  body { background:#0C0B09; color:rgba(240,232,216,.9); font-family:'Josefin Sans',sans-serif; font-weight:300; min-height:100vh; }

  @keyframes rsFadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
  @keyframes rsSpin   { to{transform:rotate(360deg)} }

  /* ── LANDING (belum login) ── */
  .rs-land { min-height:100vh; display:flex; flex-direction:column; }

  /* hero */
  .rs-hero { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:100px 28px 70px; position:relative; overflow:hidden; }
  .rs-hero-glow { position:absolute; top:-10%; left:50%; transform:translateX(-50%); width:70%; height:80%; background:radial-gradient(ellipse 65% 50% at 50% 30%, rgba(201,168,76,.05), transparent 65%); pointer-events:none; }
  .rs-hero-grid { position:absolute; inset:0; background-image:repeating-linear-gradient(0deg,rgba(201,168,76,.015) 0px,rgba(201,168,76,.015) 1px,transparent 1px,transparent 60px),repeating-linear-gradient(90deg,rgba(201,168,76,.015) 0px,rgba(201,168,76,.015) 1px,transparent 1px,transparent 60px); pointer-events:none; }
  .rs-hero-tag   { font-family:'Cinzel',serif; font-size:.44rem; letter-spacing:.56em; color:rgba(201,168,76,.5); margin-bottom:20px; position:relative; }
  .rs-hero-title { font-family:'Cinzel',serif; font-size:clamp(2.2rem,7vw,5rem); letter-spacing:.03em; line-height:.95; margin-bottom:18px; position:relative; }
  .rs-hero-title span { color:#C9A84C; font-family:'Cormorant Garamond',serif; font-style:italic; }
  .rs-hero-sub   { font-family:'Cormorant Garamond',serif; font-style:italic; font-size:clamp(.9rem,2vw,1.1rem); color:rgba(240,232,216,.32); max-width:480px; line-height:1.9; margin-bottom:44px; position:relative; }

  /* benefits grid */
  .rs-benefits { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; max-width:820px; width:100%; margin-bottom:44px; position:relative; }
  .rs-benefit   { border:1px solid rgba(201,168,76,.1); padding:20px 18px; text-align:left; }
  .rs-ben-icon  { font-size:1.3rem; margin-bottom:10px; }
  .rs-ben-title { font-family:'Cinzel',serif; font-size:.5rem; letter-spacing:.16em; color:rgba(240,232,216,.82); margin-bottom:5px; }
  .rs-ben-desc  { font-size:.43rem; letter-spacing:.08em; color:rgba(240,232,216,.3); line-height:1.8; }
  .rs-ben-val   { font-family:'Cinzel',serif; font-size:.88rem; color:#C9A84C; margin-top:8px; }

  /* commission table */
  .rs-tier-table { max-width:560px; width:100%; border:1px solid rgba(201,168,76,.1); position:relative; margin-bottom:44px; }
  .rs-tier-head  { display:grid; grid-template-columns:1fr 1fr 1fr; padding:10px 18px; background:rgba(201,168,76,.05); border-bottom:1px solid rgba(201,168,76,.1); }
  .rs-tier-hcell { font-family:'Cinzel',serif; font-size:.38rem; letter-spacing:.32em; color:rgba(201,168,76,.5); }
  .rs-tier-row   { display:grid; grid-template-columns:1fr 1fr 1fr; padding:11px 18px; border-bottom:1px solid rgba(255,255,255,.04); }
  .rs-tier-row:last-child { border-bottom:none; }
  .rs-tier-cell  { font-size:.44rem; letter-spacing:.1em; color:rgba(240,232,216,.6); }
  .rs-tier-cell.hi { color:#C9A84C; font-family:'Cinzel',serif; }

  /* CTA buttons */
  .rs-hero-actions { display:flex; gap:12px; flex-wrap:wrap; justify-content:center; position:relative; }
  .rs-btn-primary  { padding:13px 32px; background:#C9A84C; color:#0C0B09; font-family:'Cinzel',serif; font-size:.52rem; letter-spacing:.26em; border:none; cursor:pointer; transition:opacity .2s; }
  .rs-btn-primary:hover { opacity:.88; }
  .rs-btn-ghost    { padding:12px 28px; background:transparent; border:1px solid rgba(201,168,76,.3); color:rgba(201,168,76,.8); font-family:'Cinzel',serif; font-size:.52rem; letter-spacing:.26em; cursor:pointer; transition:all .25s; }
  .rs-btn-ghost:hover { background:rgba(201,168,76,.06); border-color:rgba(201,168,76,.55); }

  /* ── LOGIN MODAL ── */
  .rs-modal-over { position:fixed; inset:0; background:rgba(0,0,0,.7); z-index:50; backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; padding:24px; }
  .rs-modal      { width:100%; max-width:380px; background:#0D0B09; border:1px solid rgba(201,168,76,.15); padding:36px 28px; animation:rsFadeUp .3s ease both; }
  .rs-modal-title { font-family:'Cinzel',serif; font-size:1.2rem; letter-spacing:.06em; margin-bottom:6px; }
  .rs-modal-sub   { font-size:.44rem; letter-spacing:.12em; color:rgba(240,232,216,.28); margin-bottom:24px; }
  .rs-modal-label { font-size:.41rem; letter-spacing:.28em; color:rgba(240,232,216,.35); display:block; margin-bottom:6px; }
  .rs-modal-inp   { width:100%; padding:11px 12px; background:rgba(255,255,255,.02); border:1px solid rgba(201,168,76,.12); color:rgba(240,232,216,.88); font-family:'Josefin Sans',sans-serif; font-size:.84rem; font-weight:300; outline:none; margin-bottom:10px; transition:border-color .2s; }
  .rs-modal-inp:focus { border-color:rgba(201,168,76,.38); }
  .rs-modal-inp::placeholder { color:rgba(240,232,216,.2); }
  .rs-modal-btn   { width:100%; padding:12px; background:#C9A84C; color:#0C0B09; font-family:'Cinzel',serif; font-size:.5rem; letter-spacing:.26em; border:none; cursor:pointer; transition:opacity .2s; }
  .rs-modal-btn:hover { opacity:.88; }
  .rs-modal-err   { font-size:.42rem; color:rgba(201,123,111,.7); margin-top:7px; }
  .rs-modal-close { float:right; background:none; border:none; color:rgba(240,232,216,.3); font-size:1rem; cursor:pointer; margin-bottom:-4px; }

  /* ── DASHBOARD ── */
  .rs-dash { min-height:100vh; display:flex; flex-direction:column; }
  .rs-topbar { padding:0 32px; height:56px; display:flex; align-items:center; justify-content:space-between; background:rgba(12,11,9,.9); border-bottom:1px solid rgba(201,168,76,.07); position:sticky; top:0; z-index:10; backdrop-filter:blur(12px); }
  .rs-topbar-logo  { font-family:'Cinzel',serif; font-size:.54rem; letter-spacing:.34em; color:rgba(201,168,76,.7); }
  .rs-topbar-right { display:flex; align-items:center; gap:14px; }
  .rs-topbar-name  { font-size:.44rem; letter-spacing:.16em; color:rgba(240,232,216,.45); }
  .rs-topbar-out   { padding:6px 14px; background:transparent; border:1px solid rgba(201,168,76,.15); color:rgba(201,168,76,.55); font-family:'Cinzel',serif; font-size:.42rem; letter-spacing:.18em; cursor:pointer; transition:all .2s; }
  .rs-topbar-out:hover { background:rgba(201,168,76,.06); }

  .rs-body    { flex:1; max-width:960px; margin:0 auto; width:100%; padding:36px 28px 80px; }

  /* stats */
  .rs-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:32px; }
  .rs-stat   { border:1px solid rgba(201,168,76,.1); padding:16px 18px; }
  .rs-stat-label { font-family:'Cinzel',serif; font-size:.38rem; letter-spacing:.38em; color:rgba(201,168,76,.42); margin-bottom:8px; }
  .rs-stat-val   { font-family:'Cinzel',serif; font-size:clamp(1.3rem,3vw,1.8rem); color:rgba(240,232,216,.9); line-height:1; }
  .rs-stat-sub   { font-size:.38rem; letter-spacing:.1em; color:rgba(240,232,216,.26); margin-top:5px; }

  /* referral link */
  .rs-ref-box   { border:1px solid rgba(201,168,76,.16); border-top:2px solid rgba(201,168,76,.5); padding:20px 22px; margin-bottom:28px; }
  .rs-ref-title { font-family:'Cinzel',serif; font-size:.44rem; letter-spacing:.38em; color:rgba(201,168,76,.55); margin-bottom:13px; }
  .rs-ref-row   { display:flex; gap:0; border:1px solid rgba(201,168,76,.18); }
  .rs-ref-url   { flex:1; padding:10px 12px; background:rgba(255,255,255,.02); border:none; color:rgba(240,232,216,.65); font-family:'Josefin Sans',sans-serif; font-size:.78rem; font-weight:300; outline:none; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; cursor:text; letter-spacing:.04em; }
  .rs-ref-copy  { padding:10px 18px; background:rgba(201,168,76,.1); border:none; border-left:1px solid rgba(201,168,76,.2); color:rgba(201,168,76,.8); font-family:'Cinzel',serif; font-size:.42rem; letter-spacing:.18em; cursor:pointer; transition:all .2s; white-space:nowrap; }
  .rs-ref-copy:hover { background:rgba(201,168,76,.2); }
  .rs-ref-note  { font-size:.4rem; letter-spacing:.1em; color:rgba(240,232,216,.25); margin-top:9px; line-height:1.8; }

  /* orders table */
  .rs-sec-title { font-family:'Cinzel',serif; font-size:.44rem; letter-spacing:.38em; color:rgba(201,168,76,.44); margin-bottom:14px; }
  .rs-table-wrap { overflow-x:auto; border:1px solid rgba(201,168,76,.09); margin-bottom:28px; }
  table { width:100%; border-collapse:collapse; min-width:580px; }
  thead tr { border-bottom:1px solid rgba(201,168,76,.12); background:rgba(201,168,76,.04); }
  th { font-family:'Cinzel',serif; font-size:.37rem; letter-spacing:.28em; color:rgba(201,168,76,.5); padding:11px 14px; text-align:left; white-space:nowrap; }
  tbody tr { border-bottom:1px solid rgba(255,255,255,.04); }
  td { padding:11px 14px; font-size:.44rem; letter-spacing:.08em; color:rgba(240,232,216,.6); vertical-align:middle; }
  .td-couple { color:rgba(240,232,216,.85); }
  .td-bride  { color:rgba(201,168,76,.75); }
  .td-price  { font-family:'Cinzel',serif; font-size:.46rem; color:rgba(240,232,216,.75); }
  .td-comm   { font-family:'Cinzel',serif; font-size:.46rem; color:#C9A84C; }
  .td-badge  { display:inline-block; padding:2px 9px; font-family:'Cinzel',serif; font-size:.35rem; letter-spacing:.14em; border:1px solid; border-radius:2px; }
  .td-time   { font-size:.4rem; color:rgba(240,232,216,.28); white-space:nowrap; }

  /* withdraw */
  .rs-withdraw-box { border:1px solid rgba(201,168,76,.1); padding:22px; }
  .rs-withdraw-row { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:14px; }
  .rs-wd-field  { display:flex; flex-direction:column; gap:6px; }
  .rs-wd-label  { font-size:.41rem; letter-spacing:.28em; color:rgba(240,232,216,.35); }
  .rs-wd-inp    { padding:10px 12px; background:rgba(255,255,255,.02); border:1px solid rgba(201,168,76,.1); color:rgba(240,232,216,.85); font-family:'Josefin Sans',sans-serif; font-size:.82rem; font-weight:300; outline:none; transition:border-color .2s; }
  .rs-wd-inp:focus { border-color:rgba(201,168,76,.38); }
  .rs-wd-inp::placeholder { color:rgba(240,232,216,.18); }
  .rs-wd-btn    { width:100%; padding:12px; background:#C9A84C; color:#0C0B09; font-family:'Cinzel',serif; font-size:.5rem; letter-spacing:.26em; border:none; cursor:pointer; transition:opacity .2s; }
  .rs-wd-btn:hover    { opacity:.88; }
  .rs-wd-btn:disabled { opacity:.35; cursor:default; }
  .rs-wd-note   { font-size:.4rem; letter-spacing:.1em; color:rgba(240,232,216,.22); margin-top:9px; line-height:1.85; text-align:center; }

  /* empty / loading */
  .rs-empty { padding:44px; text-align:center; font-family:'Cinzel',serif; font-size:.48rem; letter-spacing:.26em; color:rgba(240,232,216,.18); }
  .rs-spin  { width:18px; height:18px; border:1px solid rgba(201,168,76,.25); border-top-color:#C9A84C; border-radius:50%; animation:rsSpin 1s linear infinite; display:inline-block; }

  /* toast */
  .rs-toast { position:fixed; bottom:24px; left:50%; transform:translateX(-50%); background:#1A1610; border:1px solid rgba(201,168,76,.3); padding:10px 22px; font-family:'Cinzel',serif; font-size:.46rem; letter-spacing:.18em; color:rgba(201,168,76,.8); z-index:200; animation:rsFadeUp .3s ease both; white-space:nowrap; }

  /* footer */
  .rs-footer { padding:28px 44px; border-top:1px solid rgba(201,168,76,.06); text-align:center; font-family:'Cinzel',serif; font-size:.4rem; letter-spacing:.26em; color:rgba(201,168,76,.28); }

  @media(max-width:768px) {
    .rs-benefits { grid-template-columns:1fr; }
    .rs-stats    { grid-template-columns:1fr 1fr; }
    .rs-withdraw-row { grid-template-columns:1fr; }
    .rs-body { padding:24px 18px 60px; }
  }
  @media(max-width:480px) {
    .rs-stats { grid-template-columns:1fr; }
    .rs-hero-actions { flex-direction:column; align-items:center; }
  }
`;

// ── LANDING PAGE ──────────────────────────────────────────────
function ResellerLanding({ onRegister, onLogin }) {
  const TIERS = [
    ["1–5 order",    "20%", "Semua harga paket"],
    ["6–20 order",   "25%", "Naik otomatis"],
    [">20 order",    "30%", "Komisi tertinggi"],
  ];

  return (
    <div className="rs-land">
      <div className="rs-hero">
        <div className="rs-hero-glow" />
        <div className="rs-hero-grid" />

        <div className="rs-hero-tag">✦ PROGRAM RESELLER ✦</div>
        <h1 className="rs-hero-title">
          Jual Undangan,<br />
          <span>Dapat Komisi</span>
        </h1>
        <p className="rs-hero-sub">
          Bantu pasangan di sekitarmu bikin undangan digital.<br />
          Dapat komisi 20–30% per order, cair ke rekening kamu.
        </p>

        {/* Benefits */}
        <div className="rs-benefits">
          {[
            { icon:"💰", title:"Komisi 20–30%",      desc:"Per order yang masuk via link referralmu.",              val:"Rp 10rb–45rb/order" },
            { icon:"🔗", title:"Link Unik",           desc:"Setiap reseller punya link khusus yang terlacak.",       val:"Auto-tracking" },
            { icon:"📊", title:"Dashboard Realtime",  desc:"Pantau order, komisi, dan riwayat withdraw kapan saja.", val:"Transparan" },
          ].map(b => (
            <div key={b.title} className="rs-benefit">
              <div className="rs-ben-icon">{b.icon}</div>
              <div className="rs-ben-title">{b.title}</div>
              <div className="rs-ben-desc">{b.desc}</div>
              <div className="rs-ben-val">{b.val}</div>
            </div>
          ))}
        </div>

        {/* Tier table */}
        <div className="rs-tier-table">
          <div className="rs-tier-head">
            {["JUMLAH ORDER","KOMISI","KETERANGAN"].map(h => (
              <div key={h} className="rs-tier-hcell">{h}</div>
            ))}
          </div>
          {TIERS.map(([o,k,ket]) => (
            <div key={o} className="rs-tier-row">
              <div className="rs-tier-cell">{o}</div>
              <div className="rs-tier-cell hi">{k}</div>
              <div className="rs-tier-cell">{ket}</div>
            </div>
          ))}
        </div>

        <div className="rs-hero-actions">
          <button className="rs-btn-primary" onClick={onRegister}>Daftar Jadi Reseller</button>
          <button className="rs-btn-ghost"   onClick={onLogin}>Sudah Punya Akun</button>
        </div>
      </div>

      <footer className="rs-footer">✦ MYKISAH · Program Reseller</footer>
    </div>
  );
}

// ── REGISTER MODAL ────────────────────────────────────────────
function RegisterModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ name:"", phone:"", email:"", bank:"", bank_number:"", bank_holder:"" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const ff = (k,v) => setForm(p => ({...p,[k]:v}));

  async function submit() {
    if (!form.name || !form.phone) { setErr("Nama dan nomor WA wajib diisi."); return; }
    setLoading(true);
    const code = "RS" + Date.now().toString(36).toUpperCase().slice(-6);
    const saved = await sbPost("resellers", {
      name:        form.name,
      phone:       form.phone,
      email:       form.email || null,
      bank:        form.bank || null,
      bank_number: form.bank_number || null,
      bank_holder: form.bank_holder || null,
      referral_code: code,
      commission_rate: 0.20,
      balance:     0,
      status:      "active",
    });
    setLoading(false);
    if (!saved) { setErr("Gagal daftar, coba lagi."); return; }
    onSuccess(saved);
  }

  return (
    <div className="rs-modal-over" onClick={onClose}>
      <div className="rs-modal" onClick={e => e.stopPropagation()}>
        <button className="rs-modal-close" onClick={onClose}>✕</button>
        <div className="rs-modal-title">Daftar Reseller</div>
        <p className="rs-modal-sub">Isi data di bawah untuk mulai.</p>

        {[
          ["name",        "Nama Lengkap *",      "text",  "Nama kamu"],
          ["phone",       "Nomor WhatsApp *",     "tel",   "08xxxxxxxxxx"],
          ["email",       "Email (opsional)",     "email", "email@kamu.com"],
          ["bank",        "Nama Bank",            "text",  "BCA / Mandiri / dll"],
          ["bank_number", "Nomor Rekening",       "text",  "0123456789"],
          ["bank_holder", "Nama Pemilik Rekening","text",  "Sesuai rekening"],
        ].map(([k,label,type,placeholder]) => (
          <div key={k}>
            <label className="rs-modal-label">{label}</label>
            <input className="rs-modal-inp" type={type} placeholder={placeholder}
              value={form[k]} onChange={e => ff(k, e.target.value)}
              onKeyDown={e => e.key==="Enter" && submit()} />
          </div>
        ))}

        <button className="rs-modal-btn" onClick={submit} disabled={loading}>
          {loading ? "Mendaftar..." : "Daftar Sekarang"}
        </button>
        {err && <p className="rs-modal-err">{err}</p>}
      </div>
    </div>
  );
}

// ── LOGIN MODAL ───────────────────────────────────────────────
function LoginModal({ onClose, onSuccess }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    if (!code.trim()) return;
    setLoading(true);
    const data = await sbGet(`resellers?referral_code=eq.${code.trim().toUpperCase()}&limit=1`);
    setLoading(false);
    if (!data || data.length === 0) { setErr("Kode tidak ditemukan."); return; }
    onSuccess(data[0]);
  }

  return (
    <div className="rs-modal-over" onClick={onClose}>
      <div className="rs-modal" onClick={e => e.stopPropagation()}>
        <button className="rs-modal-close" onClick={onClose}>✕</button>
        <div className="rs-modal-title">Masuk Dashboard</div>
        <p className="rs-modal-sub">Masukkan kode referralmu.</p>
        <label className="rs-modal-label">Kode Referral</label>
        <input className="rs-modal-inp" placeholder="RSxxxxxx"
          value={code} onChange={e => setCode(e.target.value.toUpperCase())}
          onKeyDown={e => e.key==="Enter" && submit()} />
        <button className="rs-modal-btn" onClick={submit} disabled={loading}>
          {loading ? "Mencari..." : "Masuk"}
        </button>
        {err && <p className="rs-modal-err">{err}</p>}
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────
function ResellerDashboard({ reseller, onLogout }) {
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [copied,   setCopied]   = useState(false);
  const [toast,    setToast]    = useState("");
  const [wd, setWd] = useState({ bank:"", number:"", holder:"", amount:"" });
  const [wdLoading, setWdLoading] = useState(false);

  const siteUrl    = window.location.origin;
  const refLink    = `${siteUrl}/order?ref=${reseller.referral_code}`;
  const commRate   = reseller.commission_rate || COMMISSION_RATE;

  function showToast(msg) {
    setToast(msg); setTimeout(() => setToast(""), 2600);
  }

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    const data = await sbGet(
      `orders?referral_code=eq.${reseller.referral_code}&order=created_at.desc&limit=100`
    );
    setOrders(data || []);
    setLoading(false);
  }, [reseller.referral_code]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  function copyRef() {
    navigator.clipboard.writeText(refLink);
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  }

  // Stats
  const totalOrders   = orders.length;
  const paidOrders    = orders.filter(o => ["payment_confirmed","in_progress","active"].includes(o.status));
  const totalSales    = paidOrders.reduce((s,o) => s + (o.total_price||0), 0);
  const totalComm     = Math.round(totalSales * commRate);
  const balance       = reseller.balance || 0;

  // Komisi tier
  const currentRate = totalOrders >= 20 ? 30 : totalOrders >= 6 ? 25 : 20;

  // Withdraw via WA
  function requestWithdraw() {
    if (!wd.amount || !wd.bank || !wd.number || !wd.holder) {
      showToast("Lengkapi data withdraw dulu."); return;
    }
    const phone = WA_ADMIN.replace(/^0/,"62").replace(/\D/g,"");
    const msg   = encodeURIComponent(
      `Halo MyKisah! Saya ingin request withdraw komisi reseller.\n\n` +
      `Nama: *${reseller.name}*\n` +
      `Kode Reseller: *${reseller.referral_code}*\n` +
      `Jumlah Withdraw: *${fmt(Number(wd.amount))}*\n` +
      `Bank: ${wd.bank}\n` +
      `No. Rekening: ${wd.number}\n` +
      `Atas Nama: ${wd.holder}\n\n` +
      `Mohon diproses. Terima kasih!`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  }

  return (
    <div className="rs-dash">
      {/* Topbar */}
      <div className="rs-topbar">
        <div className="rs-topbar-logo">✦ MYKISAH Reseller</div>
        <div className="rs-topbar-right">
          <span className="rs-topbar-name">{reseller.name}</span>
          <button className="rs-topbar-out" onClick={onLogout}>Keluar</button>
        </div>
      </div>

      <div className="rs-body">

        {/* Stats */}
        <div className="rs-stats">
          {[
            ["TOTAL ORDER",   totalOrders,          "semua via referral"],
            ["TOTAL PENJUALAN",fmt(totalSales),      "sudah dikonfirmasi"],
            ["TOTAL KOMISI",  fmt(totalComm),        `rate ${currentRate}%`],
            ["SALDO",         fmt(balance),          "siap dicairkan"],
          ].map(([l,v,s]) => (
            <div key={l} className="rs-stat">
              <div className="rs-stat-label">{l}</div>
              <div className="rs-stat-val">{v}</div>
              <div className="rs-stat-sub">{s}</div>
            </div>
          ))}
        </div>

        {/* Referral link */}
        <div className="rs-ref-box">
          <div className="rs-ref-title">✦ LINK REFERRALMU</div>
          <div className="rs-ref-row">
            <input className="rs-ref-url" readOnly value={refLink}
              onFocus={e => e.target.select()} />
            <button className="rs-ref-copy" onClick={copyRef}>
              {copied ? "Disalin ✓" : "Salin"}
            </button>
          </div>
          <p className="rs-ref-note">
            Bagikan link ini ke calon customer. Setiap order yang masuk via link ini otomatis tercatat
            sebagai referralmu. Kode: <strong style={{color:"rgba(201,168,76,.7)"}}>{reseller.referral_code}</strong>
          </p>
        </div>

        {/* Order history */}
        <div className="rs-sec-title">RIWAYAT ORDER</div>
        <div className="rs-table-wrap">
          {loading ? (
            <div className="rs-empty"><span className="rs-spin" /></div>
          ) : orders.length === 0 ? (
            <div className="rs-empty">Belum ada order via link referralmu</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>ORDER</th>
                  <th>NAMA</th>
                  <th>TOTAL</th>
                  <th>KOMISI</th>
                  <th>STATUS</th>
                  <th>WAKTU</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => {
                  const isPaid = ["payment_confirmed","in_progress","active"].includes(o.status);
                  const comm   = isPaid ? Math.round((o.total_price||0) * commRate) : 0;
                  const statusLabel = {
                    pending_payment:   "Pending",
                    payment_uploaded:  "Bukti Masuk",
                    payment_confirmed: "Dikonfirmasi",
                    in_progress:       "Dikerjakan",
                    active:            "Aktif",
                    expired:           "Expired",
                  }[o.status] || o.status;

                  return (
                    <tr key={o.order_number}>
                      <td style={{fontFamily:"'Cinzel',serif",fontSize:".42rem",letterSpacing:".16em",color:"rgba(201,168,76,.55)"}}>
                        {o.order_number}
                      </td>
                      <td className="td-couple">
                        {o.groom_name} <span style={{opacity:.3}}>&</span>{" "}
                        <span className="td-bride">{o.bride_name}</span>
                      </td>
                      <td className="td-price">{fmt(o.total_price)}</td>
                      <td className="td-comm">{isPaid ? fmt(comm) : <span style={{opacity:.3}}>—</span>}</td>
                      <td>
                        <span className="td-badge" style={{
                          color: o.status==="active" ? "#C9A84C" : "rgba(240,232,216,.45)",
                          borderColor: o.status==="active" ? "rgba(201,168,76,.35)" : "rgba(255,255,255,.1)",
                          background:  o.status==="active" ? "rgba(201,168,76,.06)" : "transparent",
                        }}>{statusLabel}</span>
                      </td>
                      <td className="td-time">{timeAgo(o.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Withdraw */}
        <div className="rs-sec-title" style={{marginTop:8}}>REQUEST WITHDRAW</div>
        <div className="rs-withdraw-box">
          <div className="rs-withdraw-row">
            {[
              ["bank",   "Nama Bank",             "text",   "BCA / Mandiri / dll"],
              ["number", "Nomor Rekening",         "text",   "0123456789"],
              ["holder", "Nama Pemilik Rekening",  "text",   "Sesuai buku tabungan"],
              ["amount", "Jumlah Withdraw (Rp)",   "number", "Min. Rp 50.000"],
            ].map(([k,l,t,p]) => (
              <div key={k} className="rs-wd-field">
                <label className="rs-wd-label">{l}</label>
                <input className="rs-wd-inp" type={t} placeholder={p}
                  value={wd[k]} onChange={e => setWd(w => ({...w,[k]:e.target.value}))} />
              </div>
            ))}
          </div>
          <button className="rs-wd-btn" onClick={requestWithdraw}
            disabled={wdLoading || !wd.amount || !wd.bank || !wd.number || !wd.holder}>
            💬 Request Withdraw via WhatsApp
          </button>
          <p className="rs-wd-note">
            Request withdraw akan dikirim ke admin via WhatsApp.<br />
            Proses pencairan 1–2 hari kerja · Min. withdraw Rp 50.000.
          </p>
        </div>

      </div>

      <footer className="rs-footer">✦ MYKISAH · Dashboard Reseller</footer>

      {toast && <div className="rs-toast">{toast}</div>}
    </div>
  );
}

// ── MAIN ─────────────────────────────────────────────────────
export default function Reseller() {
  const [modal,    setModal]    = useState(null); // null | "register" | "login"
  const [reseller, setReseller] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("mk_reseller") || "null"); }
    catch { return null; }
  });

  function handleSuccess(data) {
    sessionStorage.setItem("mk_reseller", JSON.stringify(data));
    setReseller(data);
    setModal(null);
  }

  function handleLogout() {
    sessionStorage.removeItem("mk_reseller");
    setReseller(null);
  }

  if (reseller) return (
    <ResellerDashboard reseller={reseller} onLogout={handleLogout} />
  );

  return (
    <>
      <style>{CSS}</style>
      <ResellerLanding
        onRegister={() => setModal("register")}
        onLogin={()    => setModal("login")}
      />
      {modal === "register" && (
        <RegisterModal onClose={() => setModal(null)} onSuccess={handleSuccess} />
      )}
      {modal === "login" && (
        <LoginModal onClose={() => setModal(null)} onSuccess={handleSuccess} />
      )}
    </>
  );
}
